import { forwardRef, TextareaHTMLAttributes } from 'react'
import { FieldErrors } from 'react-hook-form'
import { cn } from '../../utils'
import FormErrorMessage from './FormErrorMessage'
import { getErrorByPath } from './getErrorByPath'

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  name?: string
  label: string
  labelSrOnly?: boolean
  errors?: FieldErrors
  wrapperClassName?: string
  activateEmptyWarning?: boolean
  'data-testid': string
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      name,
      label,
      labelSrOnly,
      'data-testid': testId,
      wrapperClassName,
      errors,
      className,
      value,
      readOnly = false,
      disabled = false,
      activateEmptyWarning = false,
      ...props
    },
    ref
  ) => {
    const { maxLength } = props

    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')

    const isInvalid = !!getErrorByPath(errors, name)

    return (
      <div
        className={cn(
          wrapperClassName,
          activateEmptyWarning &&
            isEmpty &&
            !disabled &&
            'ring-2 ring-red-400 rounded-md'
        )}
        data-testid={`${testId}-wrapper-outer`}
      >
        <div data-testid={`${testId}-wrapper-inner`}>
          <label
            className={cn('elm-label', { 'sr-only': labelSrOnly })}
            htmlFor={id}
            id={`${id}-label`}
            data-testid={`${testId}-label`}
          >
            {label}
          </label>
          <textarea
            {...props}
            id={id}
            name={name}
            className={cn(
              'elm-textarea',
              '!mt-0',
              className,
              'aria-[invalid=true]:!border-b-red-500'
            )}
            aria-invalid={isInvalid}
            aria-readonly={readOnly ? 'true' : 'false'}
            aria-labelledby={`${id}-label`}
            data-variant="solid"
            ref={ref}
            data-testid={testId}
            maxLength={maxLength}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        {name && errors && (
          <FormErrorMessage
            errors={errors}
            name={name}
            data-testid={`${testId}-error-message`}
          />
        )}
      </div>
    )
  }
)

TextArea.displayName = 'TextArea'

export default TextArea
